import useChartSelections from "components/charting/hooks/useChartSelections";
import { PolynomialRegressionProcessor } from "components/charting/processors/PolynomialRegressionProcessor";
import { ProfilingChartEntityId, SelectedChartMeasureExercise } from "components/charting/types";
import { LineData } from "components/charting/types/Chart.types";
import ExerciseHelpers from "helpers/ExerciseHelpers";
import usePreferences from "hooks/usePreferences";
import { useMemo } from "react";
import { Measurement, MetadataType } from "types";
import useProfilingRepMetric from "../useProfilingRepMetric";

interface ProfilingMeasurementBestMetrics {
  measurement: Measurement;
  weight: number;
  best: {
    field: string;
    value: number;
  };
}

function useProfilingLine(measurements: Measurement[], profilingChartEntityId: ProfilingChartEntityId) {
  const selections = useChartSelections();
  const measure = selections.measures[0] as SelectedChartMeasureExercise;
  const source = selections.sources[0];
  const [prefs] = usePreferences();
  const repMetric = useProfilingRepMetric();

  const measurementsWithBestRepMetric: ProfilingMeasurementBestMetrics[] = useMemo(
    () =>
      measurements
        .map((measurement) => {
          const weight = measurement.metrics.find((metric) => metric?.field === "weight")?.value;
          const best = measurement.repetitions.reduce<{ field: string; value: number } | undefined>(
            (best, repetitionMetrics) => {
              const metric = repetitionMetrics.find((repetitionMetric) => repetitionMetric.field === repMetric?.field);

              if (best && metric && best.value <= metric.value) return metric;

              return best || metric;
            },
            undefined
          );

          return { measurement, weight, best };
        })
        .filter((m) => m.weight && m.best) as ProfilingMeasurementBestMetrics[],
    [measurements, repMetric?.field]
  );

  const data: LineData[] = useMemo(() => {
    const getCommon = (measurement: Measurement) => ({
      entityId: profilingChartEntityId,
      source,
      measure: { id: measure.id, metricField: measure.metric?.field, repMetricField: repMetric?.field },
      metadataType: MetadataType.EXERCISE,
      sortOrder: repMetric?.sortType === 0 ? ("asc" as const) : ("desc" as const),
      variant: measurement.variant || undefined,
    });

    const rawDatasets = measurementsWithBestRepMetric.map((measurement): [number, number] => [
      measurement.weight,
      measurement.best.value,
    ]);

    const leastSquaresProcessor = new PolynomialRegressionProcessor(
      rawDatasets,
      prefs.imperialSystemEnabled,
      ExerciseHelpers.isPowerMetric(repMetric?.field)
        ? PolynomialRegressionProcessor.degrees.QUADRATIC
        : PolynomialRegressionProcessor.degrees.LINEAR
    );

    // The line is one side's only when every set is from that side; mixed sides leave it without one.
    const setVariants = measurementsWithBestRepMetric.map(({ measurement }) => measurement.variant || undefined);
    const lineVariant = setVariants.every((variant) => variant === setVariants[0]) ? setVariants[0] : undefined;

    const profilingLine = measurementsWithBestRepMetric.slice(0, 1).map(({ measurement }): LineData => {
      const common = { ...getCommon(measurement), variant: lineVariant };
      const lineMeasurement = { ...measurement, completedDate: "" };
      return {
        ...common,
        line: leastSquaresProcessor.line.map(([x, y]) => ({
          ...common,
          x,
          y,
          metadataType: MetadataType.EXERCISE,
          measurement: lineMeasurement,
          profiling: {
            x,
            y,
            side: profilingChartEntityId,
          },
        })),
      };
    });

    const selectedSetMeasurements = measurementsWithBestRepMetric.map(({ measurement, weight, best }): LineData => {
      const common = getCommon(measurement);
      const entityId = [profilingChartEntityId, measurement.id].join("-");

      return {
        ...common,
        entityId,
        line: [
          {
            ...common,
            entityId,
            x: weight,
            y: best.value,
            metadataType: MetadataType.EXERCISE,
            measurement,
            profiling: {
              x: weight,
              y: best.value,
              side: profilingChartEntityId,
            },
          },
        ],
      };
    });
    return profilingLine.concat(selectedSetMeasurements).filter(Boolean);
  }, [
    measure.id,
    measure.metric?.field,
    measurementsWithBestRepMetric,
    prefs.imperialSystemEnabled,
    profilingChartEntityId,
    repMetric?.field,
    repMetric?.sortType,
    source,
  ]);

  return data;
}

export default useProfilingLine;
